import React, { useState, useEffect } from 'react';
import { API_PATHS } from '../../../utils/constants';
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  ContentCopy as DuplicateIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ToggleOn as ActivateIcon,
  ToggleOff as DeactivateIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { getAuthToken } from '../../../utils/authUtils';
import workflowService from '../../../services/workflow';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormLabel from '@mui/material/FormLabel';

const WorkflowList = () => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [targetTypeFilter, setTargetTypeFilter] = useState('all');
  const [workflowTypes, setWorkflowTypes] = useState([]);
  const [useMockData, setUseMockData] = useState(false); // State for controlling mock data usage - always false to ensure real data only
  const navigate = useNavigate();
  
  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [deleteOption, setDeleteOption] = useState('soft');
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const openFilterMenu = Boolean(filterAnchorEl);
  
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Function to normalize workflow data to a consistent format
  const normalizeWorkflowData = (workflows) => {
    console.log('Raw workflow data:', workflows);
    
    const normalized = workflows.map(workflow => {
      // Extract active status correctly - handle both Boolean and boolean
      // In Java, the property is 'active', but in JS we use 'isActive'
      const activeStatus = workflow.active !== undefined ? workflow.active : 
                          (workflow.isActive !== undefined ? workflow.isActive : true);
      
      console.log(`Workflow ${workflow.id || 'new'} active status:`, 
                 workflow.active, workflow.isActive, 'normalized to:', activeStatus);
      
      // Check if this is the new format (with definition field)
      if (workflow.definition) {
        let parsedDefinition = workflow.definition;
        
        // Handle definition as string (from JSONB in PostgreSQL)
        if (typeof workflow.definition === 'string') {
          try {
            console.log(`Parsing definition for workflow ${workflow.id}:`, workflow.definition);
            // Handle already escaped JSON strings (with double backslashes)
            const definitionStr = workflow.definition.replace(/\\\\([\\"'])/g, '\\$1');
            parsedDefinition = JSON.parse(definitionStr);
            console.log('Parsed definition:', parsedDefinition);
          } catch (error) {
            console.error(`Error parsing definition for workflow ${workflow.id}:`, error);
            // Try one more time with a different approach for escaped JSON
            try {
              // The definition might be doubly escaped JSON string
              console.log('Trying alternative parsing method...');
              // This format: "{\"type\": \"new-request\", ...}"
              const cleanDefinition = workflow.definition.replace(/^"/, '').replace(/"$/, '');
              parsedDefinition = JSON.parse(cleanDefinition);
              console.log('Successfully parsed with alternative method:', parsedDefinition);
            } catch (secondError) {
              console.error('Alternative parsing also failed:', secondError);
              parsedDefinition = { type: 'unknown', steps: [], variant: 'unknown' };
            }
          }
        }
        
        // Extract workflow type and steps from the parsed definition
        const workflowType = parsedDefinition.type || 'unknown';
        const variant = parsedDefinition.variant || '';
        const steps = parsedDefinition.steps || [];
        
        console.log(`Workflow ${workflow.id} type:`, workflowType);
        console.log(`Workflow ${workflow.id} variant:`, variant);
        console.log(`Workflow ${workflow.id} steps:`, steps.length);
        if (steps.length > 0) {
          console.log('First step example:', steps[0]);
        }
        
        return {
          id: workflow.id, // Use real UUID from database
          name: workflow.name,
          description: workflow.description,
          workflowType: workflowType,
          variant: variant,
          targetType: workflow.targetType || 'EMPLOYEE',
          version: workflow.version || 1,
          isActive: activeStatus,
          steps: steps,
          stepsCount: steps.length,
          createdAt: workflow.createdAt || new Date().toISOString(),
          rawDefinition: parsedDefinition, // Store the raw parsed definition for reference
          organizationDocumentTypes: workflow.organizationDocumentTypes || []
        };
      }
      // Already in the expected format
      return {
        ...workflow,
        workflowType: workflow.workflowType || 'unknown',
        isActive: activeStatus,
        steps: workflow.steps || [],
        stepsCount: (workflow.steps || []).length
      };
    });
    
    // Extract unique workflow types for filtering
    const types = [...new Set(normalized.filter(wf => wf.workflowType).map(wf => wf.workflowType))];
    setWorkflowTypes(types);
    
    return normalized;
  };

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get auth token for authenticated requests
      const token = getAuthToken();
      
      // Only use real data from API
      console.log('%c[DEBUG] Fetching REAL workflow data...', 'background: #222; color: #00ff00; font-weight: bold');
      
      // Try both authenticated and public endpoints
      let response;
      let endpointUsed = '';
      
      // First try the authenticated endpoint
      if (token) {
        try {
          console.log('%c[DEBUG] Trying authenticated endpoint with token', 'background: #222; color: #00ff00');
          response = await fetch(`${API_PATHS.PUBLIC}/workflows`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          endpointUsed = 'authenticated';
          console.log('%c[DEBUG] Authenticated API response status:', 'background: #222; color: #bada55', response.status);
        } catch (authError) {
          console.warn('Error using authenticated endpoint:', authError);
          response = null;
        }
      }
      
      // If authenticated endpoint failed or no token, try public endpoint
      if (!response || !response.ok) {
        console.log('%c[DEBUG] Trying public endpoint as fallback', 'background: #222; color: #00ff00');
        response = await fetch(`${API_PATHS.PUBLIC}/workflows`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        endpointUsed = 'public';
        console.log('%c[DEBUG] Public API response status:', 'background: #222; color: #bada55', response.status);
      }
      
      if (response.ok) {
        const apiData = await response.json();
        console.log('%c[DEBUG] Successfully fetched real workflow data:', 'background: #222; color: #00ff00', apiData);
        
        // Filter for the specific workflow if ID is provided
        const specificId = '8288028a-64be-4663-9541-174713ab55af';
        console.log('Looking for specific workflow with ID:', specificId);
        
        const filteredData = apiData.filter(workflow => {
          // If we're looking for a specific ID and this is it, log details
          if (workflow.id === specificId) {
            console.log('Found specific workflow:', workflow);
            console.log('Definition:', workflow.definition);
          }
          return true; // Keep all workflows in the list
        });
        
        setWorkflows(normalizeWorkflowData(filteredData));
      } else {
        console.error('%c[DEBUG] API response not OK:', 'background: #ff0000; color: #ffffff', response.status);
        throw new Error(`Failed to fetch workflows: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('%c[DEBUG] Error fetching workflows:', 'background: #ff0000; color: #ffffff', error);
      setError('Failed to fetch workflows from the API. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkflows();
  }, [useMockData]);

  const handleCreateWorkflow = () => {
    // Check the current path to determine which route pattern to use
    const currentPath = window.location.pathname;
    if (currentPath.includes('ADM-Workflow')) {
      // If we're in the ADM-Workflow route pattern
      navigate('/admin/workflow/create');
    } else {
      // Default to admin/workflow pattern
      navigate('/admin/workflow/create');
    }
  };

  const handleViewWorkflow = (id, workflow) => {
    console.log('Viewing workflow:', id, workflow);
    
    // Store the selected workflow in localStorage for the detail view to access
    try {
      // Save this specific workflow to localStorage
      localStorage.setItem('selected_workflow', JSON.stringify(workflow));
      console.log('Saved workflow to localStorage:', workflow);
    } catch (error) {
      console.error('Error saving workflow to localStorage:', error);
    }
    
    // Check the current path to determine which route pattern to use
    const currentPath = window.location.pathname;
    if (currentPath.includes('ADM-Workflow')) {
      // If we're in the ADM-Workflow route pattern
      console.log('Navigating to ADM-Workflow path:', `/ADM-Workflow/${id}`);
      navigate(`/ADM-Workflow/${id}`);
    } else {
      // Default to admin/workflow pattern
      console.log('Navigating to admin/workflow path:', `/admin/workflow/${id}`);
      navigate(`/admin/workflow/${id}`);
    }
  };

  const handleEditWorkflow = (id, workflow) => {
    // Store the workflow data in localStorage for the creator to use
    localStorage.setItem('workflow_to_edit', JSON.stringify(workflow));
    
    // Navigate to the creator with the edit flag
    navigate(`/admin/workflow/create?edit=${id}`);
  };

  const handleAssignWorkflow = (id, workflow) => {
    // Check the current path to determine which route pattern to use
    const currentPath = window.location.pathname;
    if (currentPath.includes('ADM-Workflow')) {
      navigate(`/admin/workflow/${id}/assign`);
    } else {
      navigate(`/admin/workflow/${id}/assign`);
    }
  };

  const handleDuplicateWorkflow = (workflow) => {
    // Create a copy of the workflow with a new ID and name
    const duplicatedWorkflow = {
      ...workflow,
      id: undefined, // Will be generated on save
      name: `${workflow.name} (Copy)`,
      isActive: false // Start as inactive
    };
    
    // Store the duplicated workflow data in localStorage for the creator to use
    localStorage.setItem('workflow_to_edit', JSON.stringify(duplicatedWorkflow));
    
    // Navigate to the creator
    navigate('/admin/workflow/create');
  };

  const handleToggleWorkflowStatus = (workflow) => {
    const updatedWorkflow = {
      ...workflow,
      isActive: !workflow.isActive
    };
    
    // Optimistically update the UI
    setWorkflows(prevWorkflows => 
      prevWorkflows.map(wf => 
        wf.id === workflow.id ? updatedWorkflow : wf
      )
    );
    
    // Try to update via API using the workflow service
    workflowService.toggleWorkflowStatus(workflow.id, !workflow.isActive)
      .then(response => {
        console.log('Successfully toggled workflow status:', response);
        
        // Refresh the entire workflow list to ensure data consistency
        fetchWorkflows();
      })
      .catch(error => {
        console.error('Error toggling workflow status:', error);
        // Revert the optimistic update
        setWorkflows(prevWorkflows => 
          prevWorkflows.map(wf => 
            wf.id === workflow.id ? workflow : wf
          )
        );
        // Show error to user
        alert(`Failed to toggle workflow status: ${error.message || 'Unknown error'}`);
      });
  };

  const filteredWorkflows = workflows.filter(workflow => {
    // First apply the search term filter
    const matchesSearch = 
      workflow.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      workflow.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      workflow.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      workflow.workflowType?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    // Then apply the type filter
    if (typeFilter !== 'all' && workflow.workflowType !== typeFilter) {
      return false;
    }
    
    // Apply target type filter
    if (targetTypeFilter !== 'all') {
      const wfTargetType = workflow.targetType || 'EMPLOYEE';
      if (wfTargetType !== targetTypeFilter) return false;
    }

    // Finally apply the status filter
    if (statusFilter === 'active' && !workflow.isActive) {
      return false;
    }
    if (statusFilter === 'inactive' && workflow.isActive) {
      return false;
    }

    return true;
  });
  
  // Handle delete workflow button click
  const handleDeleteClick = (workflow) => {
    setWorkflowToDelete(workflow);
    setDeleteDialogOpen(true);
  };

  // Handle delete dialog close
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setWorkflowToDelete(null);
    setDeleteOption('soft');
    setDeleteLoading(false);
  };

  // Handle delete workflow confirmation
  const handleDeleteConfirm = async () => {
    if (!workflowToDelete) return;
    
    try {
      setDeleteLoading(true);
      const forceDelete = deleteOption === 'force';
      
      await workflowService.deleteWorkflow(workflowToDelete.id, forceDelete);
      
      // Update the UI by removing the deleted workflow
      setWorkflows(prevWorkflows => 
        prevWorkflows.filter(wf => wf.id !== workflowToDelete.id)
      );
      
      // Close the dialog
      handleDeleteDialogClose();
    } catch (error) {
      console.error('Error deleting workflow:', error);
      // Check if the error is due to existing instances
      if (error.response?.status === 409) {
        alert('This workflow has active instances. Please use force delete option if you want to proceed.');
      } else {
        alert(`Failed to delete workflow: ${error.response?.data?.message || error.message}`);
      }
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          Workflow Management
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={useMockData}
                onChange={(e) => setUseMockData(e.target.checked)}
                color="primary"
              />
            }
            label="Use Mock Data"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateWorkflow}
          >
            Create Workflow
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TextField
            placeholder="Search workflows..."
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mr: 2 }}
          />
          <Button
            variant="contained"
            startIcon={<FilterIcon />}
            onClick={handleFilterClick}
            size="medium"
            sx={{ minWidth: 100, bgcolor: '#064d51', '&:hover': { bgcolor: '#00796b' } }}
          >
            Filter
          </Button>
        </Box>
        
        <Menu
          anchorEl={filterAnchorEl}
          open={openFilterMenu}
          onClose={handleFilterClose}
          PaperProps={{
            sx: { width: 300, p: 1 }
          }}
        >
          <Typography variant="subtitle1" sx={{ px: 2, py: 1 }}>
            Filter Workflows
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ px: 2, py: 1 }}>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Workflow Type</InputLabel>
              <Select
                value={typeFilter}
                label="Workflow Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                {workflowTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Target Type</InputLabel>
              <Select
                value={targetTypeFilter}
                label="Target Type"
                onChange={(e) => setTargetTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="EMPLOYEE">Employee</MenuItem>
                <MenuItem value="ORGANIZATION">Organization</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2 }}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => {
                setTypeFilter('all');
                setStatusFilter('all');
                setTargetTypeFilter('all');
              }}
              sx={{ mr: 1 }}
            >
              Reset
            </Button>
            <Button 
              variant="contained" 
              size="small"
              onClick={handleFilterClose}
            >
              Apply
            </Button>
          </Box>
        </Menu>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={fetchWorkflows}>
              Retry
            </Button>
          </Box>
        ) : (
          <Box>
            {filteredWorkflows.length === 0 ? (
              <Typography variant="body1" sx={{ textAlign: 'center', py: 3 }}>
                No workflows found.
              </Typography>
            ) : (
              filteredWorkflows.map((workflow) => (
                <Paper
                  key={workflow.id}
                  elevation={1}
                  sx={{
                    p: 2,
                    mb: 2,
                    '&:hover': {
                      boxShadow: 3,
                    },
                    borderLeft: workflow.isActive ? '4px solid #4caf50' : '4px solid #f44336'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6" component="h2">
                        {workflow.name}
                      </Typography>
                      <Typography variant="caption" display="block" color="textSecondary">
                        ID: {workflow.id}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mr: 1 }}>
                          <Chip
                            label={workflow.workflowType || 'Unknown'}
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                          {workflow.variant && (
                            <Chip
                              label={workflow.variant}
                              color="secondary"
                              variant="outlined"
                              size="small"
                            />
                          )}
                          <Chip
                            label={workflow.targetType === 'ORGANIZATION' ? 'Organization' : 'Employee'}
                            color={workflow.targetType === 'ORGANIZATION' ? 'warning' : 'default'}
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                        <Chip
                          label={workflow.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={workflow.isActive ? 'success' : 'error'}
                          sx={{ mr: 1 }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">v{workflow.version || 1}</Typography>
                          <Chip 
                            label={`${workflow.stepsCount || 0} steps`}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                      <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
                        {workflow.modifiedAt ? `Updated: ${new Date(workflow.modifiedAt).toLocaleDateString()}` : 
                        workflow.modifyDateTime ? `Updated: ${new Date(workflow.modifyDateTime).toLocaleDateString()}` : ''}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        {workflow.description}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          Steps: {workflow.steps.length}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <IconButton
                        color="primary"
                        onClick={() => handleViewWorkflow(workflow.id, workflow)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton onClick={() => handleEditWorkflow(workflow.id, workflow)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleAssignWorkflow(workflow.id, workflow)}>
                        <AssignmentIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDuplicateWorkflow(workflow)}>
                        <DuplicateIcon />
                      </IconButton>
                      <Tooltip title={workflow.isActive ? "Deactivate workflow" : "Activate workflow"}>
                        <IconButton 
                          onClick={() => handleToggleWorkflowStatus(workflow)}
                          color={workflow.isActive ? "success" : "default"}
                        >
                          {workflow.isActive ? <ActivateIcon /> : <DeactivateIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete workflow">
                        <IconButton 
                          onClick={() => handleDeleteClick(workflow)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="textSecondary">
                      Created: {workflow.createDateTime ? new Date(workflow.createDateTime).toLocaleDateString() : 
                              workflow.createdAt ? new Date(workflow.createdAt).toLocaleDateString() : 
                              'Not available'}
                    </Typography>
                    {(workflow.modifyDateTime || workflow.modifiedAt) && (
                      <Typography variant="body2" color="textSecondary">
                        Last Modified: {workflow.modifyDateTime ? new Date(workflow.modifyDateTime).toLocaleDateString() :
                                      workflow.modifiedAt ? new Date(workflow.modifiedAt).toLocaleDateString() :
                                      'Not available'}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        )}
      </Paper>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Workflow
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the workflow "{workflowToDelete?.name}"?
            <br /><br />
            <strong>Warning:</strong> This action cannot be undone.
          </DialogContentText>
          
          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <FormLabel component="legend">Deletion Options</FormLabel>
            <RadioGroup
              aria-label="delete-option"
              name="delete-option"
              value={deleteOption}
              onChange={(e) => setDeleteOption(e.target.value)}
            >
              <FormControlLabel 
                value="soft" 
                control={<Radio />} 
                label="Standard delete (fails if there are active workflow instances)" 
              />
              <FormControlLabel 
                value="force" 
                control={<Radio />} 
                label="Force delete (removes workflow even if there are active instances)" 
              />
            </RadioGroup>
          </FormControl>
          
          {deleteOption === 'force' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Force deletion will remove the workflow definition even if there are active workflow instances.
              This may cause issues with existing requests that use this workflow.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkflowList;
